import { toastBus } from '@/components/generalComponents/AppToast.vue';

/**
 * Toast notification service
 *
 * Usage:
 *   import toast from '@/services/toast.service';
 *
 *   toast.success('Operation completed successfully');
 *   toast.error('Something went wrong');
 *   toast.info('Please note...');
 *   toast.warning('Be careful!');
 *
 *   // With title
 *   toast.success('Your changes have been saved', 'Saved');
 *
 *   // With custom duration (ms)
 *   toast.error('Connection failed', 'Error', 10000);
 *
 *   // Persistent toast (no auto-dismiss)
 *   toast.error('Critical error occurred', 'Error', 0);
 */

const toastService = {
  /**
   * Show a success toast
   * @param {string} message - The message to display
   * @param {string} [title] - Optional title
   * @param {number} [duration=5000] - Duration in ms (0 for persistent)
   */
  success(message, title = null, duration = 5000) {
    toastBus.$emit('show-toast', {
      type: 'success',
      message,
      title,
      duration
    });
  },

  /**
   * Show an error toast
   * @param {string} message - The message to display
   * @param {string} [title] - Optional title
   * @param {number} [duration=7000] - Duration in ms (0 for persistent)
   */
  error(message, title = null, duration = 7000) {
    toastBus.$emit('show-toast', {
      type: 'error',
      message,
      title,
      duration
    });
  },

  /**
   * Show an info toast
   * @param {string} message - The message to display
   * @param {string} [title] - Optional title
   * @param {number} [duration=5000] - Duration in ms (0 for persistent)
   */
  info(message, title = null, duration = 5000) {
    toastBus.$emit('show-toast', {
      type: 'info',
      message,
      title,
      duration
    });
  },

  /**
   * Show a warning toast
   * @param {string} message - The message to display
   * @param {string} [title] - Optional title
   * @param {number} [duration=6000] - Duration in ms (0 for persistent)
   */
  warning(message, title = null, duration = 6000) {
    toastBus.$emit('show-toast', {
      type: 'warning',
      message,
      title,
      duration
    });
  },

  /**
   * Show a custom toast
   * @param {Object} options - Toast options
   * @param {string} options.type - Toast type: 'success', 'error', 'info', 'warning'
   * @param {string} options.message - The message to display
   * @param {string} [options.title] - Optional title
   * @param {number} [options.duration=5000] - Duration in ms (0 for persistent)
   */
  show(options) {
    toastBus.$emit('show-toast', options);
  }
};

export default toastService;
